'use client'

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useGetReviewsProductMutation, useGetProductCatalogMutation } from '@/shared/api-rtk/index.js'
import DescriptionChapter from '@/entities/catalog-inside-page/DescriptionChapter'
import { useInView } from 'react-intersection-observer'
import ReviewsChapter from '@/entities/catalog-inside-page/ReviewsChapter/index.js'
import FaqChapter from '@/entities/catalog-inside-page/FaqChapter/index.js'
import BlogChapter from '@/entities/catalog-inside-page/BlogChapter/index.js'
import { TOAST_ERROR } from '@/shared/constants/toastTypes.js'
import ProtocolsChapter from '@/entities/catalog-inside-page/ProtocolsChapter/index.jsx'
import DocumentsChapter from '@/entities/catalog-inside-page/DocumentsChapter/index.jsx'
import { 
  ChevronRight, 
  ChevronLeft,
  MessageSquare, 
  FileText, 
  HelpCircle, 
  BookOpen, 
  File, 
  ClipboardCheck 
} from 'lucide-react'

// Базовые вкладки без Описания (Описание будет вверху каждой вкладки)
const baseMenuItems = ['Отзывы', 'Вопросы', 'Статьи']
const documentsItem = 'Документы'
const protocolItem = 'Протокол'

// Иконки для мобильных табов
const mobileTabIcons = {
  'Отзывы': MessageSquare,
  'Вопросы': HelpCircle,
  'Статьи': BookOpen,
  'Документы': File,
  'Протокол': ClipboardCheck
}

export const TabBarNav = ({ product, productId }) => {
    const [hasDocuments, setHasDocuments] = useState(false)
    const [hasProtocols, setHasProtocols] = useState(false)
    const [menuItems, setMenuItems] = useState([])
    const [initialTab, setInitialTab] = useState('')
    const [showContent, setShowContent] = useState('')
    const [getReviews] = useGetReviewsProductMutation()
    const [getProduct] = useGetProductCatalogMutation()
    const [reviews, setReviews] = useState([])
    const [isMobile, setIsMobile] = useState(false)
    const [touchStart, setTouchStart] = useState(null)
    const [touchEnd, setTouchEnd] = useState(null)
    const menuRef = useRef(null)
    const tabsContainerRef = useRef(null)
    const contentRef = useRef(null)
    // Рефы для каждого типа контента
    const protocolsRef = useRef(null)
    const documentsRef = useRef(null)
    const reviewsRef = useRef(null)
    const faqRef = useRef(null)
    const blogRef = useRef(null)
    // Флаг для отслеживания клика по вкладке
    const isTabClick = useRef(false)
    // Флаг для отслеживания первого рендера
    const isFirstRender = useRef(true)
    
    const { inView, ref } = useInView({
        threshold: 0,
        triggerOnce: true,
    })

    // Определяем мобильное устройство
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        
        checkMobile()
        window.addEventListener('resize', checkMobile)
        
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    // Проверяем наличие документов и протоколов у товара
    useEffect(() => {
        const checkDocumentsAndProtocols = async () => {
            try {
                const response = await getProduct(productId).unwrap()
                
                if (response.result === 'success') {
                    const productData = Object.values(response.data)[0]
                    
                    const hasDocumentsData = productData.documents && 
                                             Object.keys(productData.documents).length > 0
                    const hasProtocolsData = productData.protocols && 
                                             Object.keys(productData.protocols).length > 0
                    
                    setHasDocuments(hasDocumentsData)
                    setHasProtocols(hasProtocolsData)
                    
                    // Формируем порядок: Протокол -> Документы -> Отзывы -> Вопросы -> Статьи
                    const menuItemsArray = []
                    
                    // Протокол (первая вкладка, если есть)
                    if (hasProtocolsData) {
                        menuItemsArray.push(protocolItem)
                    }
                    
                    // Документы (вторая вкладка, если есть)
                    if (hasDocumentsData) {
                        menuItemsArray.push(documentsItem)
                    }
                    
                    // Добавляем базовые вкладки
                    menuItemsArray.push(...baseMenuItems)
                    
                    setMenuItems(menuItemsArray)
                    
                    // Устанавливаем первую вкладку: Протокол -> Документы -> Отзывы
                    if (hasProtocolsData) {
                        setInitialTab(protocolItem)
                    } else if (hasDocumentsData) {
                        setInitialTab(documentsItem)
                    } else {
                        setInitialTab('Отзывы')
                    }
                }
            } catch (error) {
                console.error('Ошибка проверки документов и протоколов:', error)
                setMenuItems(baseMenuItems)
                setInitialTab('Отзывы')
            }
        }
        
        checkDocumentsAndProtocols()
    }, [productId, getProduct])

    // Устанавливаем начальную вкладку после формирования menuItems
    useEffect(() => {
        if (initialTab && !showContent) {
            setShowContent(initialTab)
            // После установки начальной вкладки сбрасываем флаг первого рендера
            setTimeout(() => {
                isFirstRender.current = false
            }, 0)
        }
    }, [initialTab, menuItems, showContent])

    // Загрузка отзывов
    useEffect(() => {
        getReviews(productId)
            .unwrap()
            .then((res) => {
                if (res.result === 'success') {
                    setReviews(Object.values(res.data).reverse())
                }
            })
            .catch(() => TOAST_ERROR('Не удалось получить отзывы'))
    }, [productId, getReviews])

    // Прокрутка меню к активному табу на мобильных
    useEffect(() => {
        if (isMobile && menuRef.current && showContent) {
            const activeTab = menuRef.current.querySelector('.mobile-tab.active')
            if (activeTab) {
                const container = menuRef.current
                const tabRect = activeTab.getBoundingClientRect()
                const containerRect = container.getBoundingClientRect()
                
                const scrollLeft = activeTab.offsetLeft - (containerRect.width / 2) + (tabRect.width / 2)
                container.scrollTo({
                    left: scrollLeft,
                    behavior: 'smooth'
                })
            }
        }
    }, [showContent, isMobile])

    // Функция для скролла к контенту выбранной вкладки (мимо описания)
    const scrollToTabContent = useCallback(() => {
        if (!isTabClick.current) return

        let targetRef = null
        
        // Выбираем нужный реф в зависимости от активной вкладки
        switch (showContent) {
            case protocolItem:
                targetRef = protocolsRef
                break
            case documentsItem:
                targetRef = documentsRef
                break
            case 'Отзывы':
                targetRef = reviewsRef
                break
            case 'Вопросы':
                targetRef = faqRef
                break
            case 'Статьи':
                targetRef = blogRef
                break
            default:
                return
        }

        // Скроллим к выбранному контенту
        if (targetRef.current) {
            // Небольшая задержка, чтобы контент точно отрендерился
            setTimeout(() => {
                targetRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start',
                    inline: 'nearest'
                })
                // Сбрасываем флаг после скролла
                setTimeout(() => {
                    isTabClick.current = false
                }, 300)
            }, 100)
        }
    }, [showContent])

    // Скроллим к контенту вкладки только при клике
    useEffect(() => {
        if (showContent && !isFirstRender.current) {
            scrollToTabContent()
        }
    }, [showContent, scrollToTabContent])

    // Обработчик свайпа - только в зоне табов
    const handleTabsTouchStart = (e) => {
        if (tabsContainerRef.current && tabsContainerRef.current.contains(e.target)) {
            setTouchStart(e.targetTouches[0].clientX)
            e.stopPropagation()
        }
    }

    const handleTabsTouchMove = (e) => {
        if (touchStart !== null && tabsContainerRef.current && tabsContainerRef.current.contains(e.target)) {
            setTouchEnd(e.targetTouches[0].clientX)
            e.stopPropagation()
        }
    }

    const handleTabsTouchEnd = (e) => {
        if (touchStart !== null && touchEnd !== null && tabsContainerRef.current) {
            const distance = touchStart - touchEnd
            const minSwipeDistance = 50
            
            if (distance > minSwipeDistance) {
                navigateToNextTab()
            }
            else if (distance < -minSwipeDistance) {
                navigateToPrevTab()
            }
            
            setTouchStart(null)
            setTouchEnd(null)
        }
        e.stopPropagation()
    }

    const navigateToNextTab = useCallback(() => {
        const currentIndex = menuItems.indexOf(showContent)
        if (currentIndex < menuItems.length - 1) {
            const nextTab = menuItems[currentIndex + 1]
            // Устанавливаем флаг, что это навигация (свайп)
            isTabClick.current = true
            setShowContent(nextTab)
            addSwipeAnimation('right')
        }
    }, [showContent, menuItems])

    const navigateToPrevTab = useCallback(() => {
        const currentIndex = menuItems.indexOf(showContent)
        if (currentIndex > 0) {
            const prevTab = menuItems[currentIndex - 1]
            // Устанавливаем флаг, что это навигация (свайп)
            isTabClick.current = true
            setShowContent(prevTab)
            addSwipeAnimation('left')
        }
    }, [showContent, menuItems])

    const addSwipeAnimation = (direction) => {
        if (contentRef.current) {
            contentRef.current.style.transform = `translateX(${direction === 'right' ? '20px' : '-20px'})`
            contentRef.current.style.opacity = '0.7'
            
            setTimeout(() => {
                if (contentRef.current) {
                    contentRef.current.style.transform = 'translateX(0)'
                    contentRef.current.style.opacity = '1'
                }
            }, 200)
        }
    }

    const handleDesktopTabClick = (tabName) => {
        // Устанавливаем флаг, что это клик по вкладке
        isTabClick.current = true
        setShowContent(tabName)
    }

    const handleMobileTabClick = (tabName) => {
        if (showContent !== tabName) {
            // Устанавливаем флаг, что это клик по вкладке
            isTabClick.current = true
            setShowContent(tabName)
        }
    }

    // Не рендерим пока не сформируется меню
    if (!menuItems.length || !showContent) {
        return null
    }

    return (
        <div
            ref={ref}
            className={`TabBarNav ${inView && 'animate__animated animate__fadeIn'}`}
        >
            {/* Только для мобильных устройств */}
            {isMobile && (
                <>
                    <div 
                        className="mobile-navigation-container"
                        ref={tabsContainerRef}
                        onTouchStart={handleTabsTouchStart}
                        onTouchMove={handleTabsTouchMove}
                        onTouchEnd={handleTabsTouchEnd}
                        style={{
                            touchAction: 'pan-y',
                            userSelect: 'none'
                        }}
                    >
                        <button 
                            className="nav-arrow prev"
                            onClick={navigateToPrevTab}
                            disabled={menuItems.indexOf(showContent) === 0}
                            aria-label="Предыдущая вкладка"
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            <ChevronLeft size={24} />
                        </button>
                        
                        <div className="mobile-tabs-wrapper">
                            <div className="mobile-tabs-scroll" ref={menuRef}>
                                {menuItems.map((itemMenu, index) => {
                                    const Icon = mobileTabIcons[itemMenu]
                                    const hasReviews = itemMenu === 'Отзывы'
                                    const isActive = showContent === itemMenu
                                    
                                    return (
                                        <button
                                            key={index}
                                            className={`mobile-tab ${isActive ? 'active' : ''}`}
                                            onClick={() => handleMobileTabClick(itemMenu)}
                                            onTouchStart={(e) => e.stopPropagation()}
                                        >
                                            <div className="mobile-tab-inner">
                                                <Icon size={20} className="mobile-tab-icon" />
                                                <span className="mobile-tab-text">
                                                    {itemMenu}
                                                    {hasReviews && reviews.length > 0 && (
                                                        <span className="mobile-tab-badge">{reviews.length}</span>
                                                    )}
                                                </span>
                                            </div>
                                            {isActive && <div className="mobile-tab-indicator" />}
                                        </button>
                                    )
                                })}
                            </div>
                            <div className="mobile-tabs-gradient left" />
                            <div className="mobile-tabs-gradient right" />
                        </div>
                        
                        <button 
                            className="nav-arrow next"
                            onClick={navigateToNextTab}
                            disabled={menuItems.indexOf(showContent) === menuItems.length - 1}
                            aria-label="Следующая вкладка"
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </>
            )}

            {/* Оригинальное меню для десктопа */}
            {!isMobile && (
                <nav className="desktop-nav">
                    {menuItems.map((itemMenu, index) => {
                        const hasReviews = itemMenu === 'Отзывы'
                        const isDisabled = showContent === itemMenu
                        
                        return (
                            <button
                                key={index}
                                disabled={isDisabled}
                                onClick={() => handleDesktopTabClick(itemMenu)}
                                className={isDisabled ? 'active' : ''}
                            >
                                {itemMenu}
                                <span>{hasReviews && ` (${reviews.length})`}</span>
                            </button>
                        )
                    })}
                </nav>
            )}

            {/* Контейнер для контента */}
            <main 
                ref={contentRef}
                className={`content-item ${isMobile ? 'mobile-content' : 'desktop-content'}`}
                style={{ 
                    transition: 'transform 0.2s ease, opacity 0.2s ease',
                    touchAction: 'auto'
                }}
            >
                {/* Описание ВСЕГДА вверху каждой вкладки */}
                <div className="description-top">
                    <DescriptionChapter
                        productDescription={product.description}
                    />
                </div>

                <div className="tab-content-wrapper">
                    {showContent === protocolItem && hasProtocols && (
                        <div ref={protocolsRef}>
                            <ProtocolsChapter
                                productId={productId}
                                productName={product.name}
                            />
                        </div>
                    )}
                    
                    {showContent === documentsItem && hasDocuments && (
                        <div ref={documentsRef}>
                            <DocumentsChapter
                                productId={productId}
                                productName={product.name}
                            />
                        </div>
                    )}
                    
                    {showContent === 'Отзывы' && (
                        <div ref={reviewsRef}>
                            <ReviewsChapter
                                product={{ productId, productName: product.name }}
                                reviews={reviews}
                            />
                        </div>
                    )}
                    
                    {showContent === 'Вопросы' && (
                        <div ref={faqRef}>
                            <FaqChapter
                                productId={productId}
                                productName={product.name}
                            />
                        </div>
                    )}
                    
                    {showContent === 'Статьи' && (
                        <div ref={blogRef}>
                            <BlogChapter productId={productId} />
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}